import React, { Component } from 'react'
import styled from 'styled-components'
import Color from 'color'
import moment from 'moment-timezone'
import { translate } from "react-i18next"
import { connect } from 'react-redux'

import { setStepWithAnswers, setReset } from 'cogsworth/utils/store'
import { getBookingFields, getCustomFields } from 'cogsworth/utils/fields'
import { setTimezone } from 'cogsworth/utils/store'
import BookingFormNavigation from './Navigation/Navigation'
import BookingFormHeader from './Header/Header'
import BookingFormStep from './Step/Step'

import BookingSummary from 'cogsworth/components/BookingSummary/BookingSummary'
import { getContrastYIQ } from 'cogsworth/utils/contrast'
import { globalTracker as tracker } from 'cogsworth/utils/Tracker'
import { getEndBookingFields } from 'cogsworth/utils/fields'
import { requiresPayOnArrival } from 'cogsworth/utils/payments'

import './BookingForm.css'

class BookingForm extends Component {
  constructor(props){
    super(props)

    this.state = {
      appointment: null,
      fields: [],
      saved: null,
      leavingStep: true,
      themedBookingForm: null,
    }

    this.goBack = this.goBack.bind(this)
    this.navigate = this.navigate.bind(this)
    this.submitAnswer = this.submitAnswer.bind(this)
    this.fieldsToAppointment = this.fieldsToAppointment.bind(this)
    this.getThemedBookingForm = this.getThemedBookingForm.bind(this)
  }

  componentWillMount() {
    this.setupTimezone()
    this.setupTheme()
    this.setupFields(this.props, true)

    const selectedService = this.props.answers && this.props.answers.booking_service
    if (this.props.step > 0 && !selectedService) {
      this.props.dispatch({ type: 'RESET' })
      window.location.reload()
    }
  }

  componentDidMount() {
    this.setState({ leavingStep: false })
  }

  componentWillReceiveProps(nextProps) {
    const { theme } = this.props

    if (nextProps.theme !== theme) {
      this.setupTheme()
    }

    // TODO: Check if answers have changed
    let isInitialSetup = false
    if (nextProps.reset) {
      this.props.dispatch(setReset(false))
      isInitialSetup = true
    }
    this.setupFields(nextProps, isInitialSetup)
  }

  // If app state does not contain a timezone, try to guess one
  setupTimezone() {
    const { dispatch, timezone } = this.props
    if (timezone === null) {
      dispatch(setTimezone(moment.tz.guess()))
    }
  }

  setupTheme() {
    const themedBookingForm = this.getThemedBookingForm()
    this.setState({ themedBookingForm })
  }

  setupFields(props, isInitialSetup) {
    const { answers, appointment, business, dispatch, features, id, pageType, step } = props
    
    const rawFields = [
      ...getBookingFields({
        id,
        business,
        appointment,
        pageType,
        answers,
        locales: {
          'no-preference': props.t('no-preference'),
          'minutes': props.t('minutes'),
          'other': props.t('other'),
        },
        setError: this.props.setError,
      }),
      ...getCustomFields({
        id,
        business,
        pageType,
        features,
        answers,
        isInitialSetup,
      }),
      ...getEndBookingFields({ business, appointment, answers })
    ]

    // Put values in from answers
    let newAnswers = {}
    const fields = [
      ...rawFields.map(field => {
        if (field.value && field.value !== answers[field.id]) {
          newAnswers[field.id] = field.value
        }
        return {
          ...field,
          value: answers[field.id],
        }
      })
    ]

    // Either update the store, to re-compute all fields
    // .. or just set the state as all the data is correct
    if (Object.keys(newAnswers).length > 0) {
      dispatch(setStepWithAnswers(step, newAnswers))
    } else {
      this.setState({ fields })
    }
  }

  // This is usually in response to clicking a UI button to answer a question
  submitAnswer(fieldId, value) {
    const { step } = this.props
    this.navigate(step + 1, {
      [fieldId]: value,
    })

    // Track event in Google Analytics
    const field = this.state.fields.find(f => f.id === fieldId)
    if(!field) {
      return
    }

    const fields = this.state.fields.filter(f => !f.hidden)
    const questionNumber = fields.findIndex(f => f.id === fieldId) + 1
    const totalQuestions = fields.length
    tracker.trackGoogleAnalyticsEvent(`Question ${questionNumber} of ${totalQuestions} | ${field.question}`, 'Cogsworth Booking Page')
  }

  goBack() {
    const { step } = this.props
    this.navigate(step - 1)
  }

  navigate(step, newAnswers = {}) {
    // TODO: clear out all answers after this step
    const { dispatch } = this.props

    this.setState({
      leavingStep: true,
    }, () => {
      setTimeout(() => {
        dispatch(setStepWithAnswers(step, newAnswers))
        this.setState({
          leavingStep: false,
        })
      }, 250)
    })
  }

  fieldsToAppointment() {
    const { rescheduling, timezone } = this.props
    const { fields } = this.state
    const systemFields = ['booking_service_category', 'booking_service', 'booking_staff', 'booking_duration', 'booking_date', 'booking_location', 'booking_guests']

    const data = {
      service: fields.find(f => f.id === 'booking_service'),
      staff: fields.find(f => f.id === 'booking_staff'),
      duration: fields.find(f => f.id === 'booking_duration'),
      date: fields.find(f => f.id === 'booking_date'),
      customer: {
        name: fields.find(f => f.id === 'name'),
        email: fields.find(f => f.id === 'email'),
        phone: fields.find(f => f.id === 'phone') || fields.find(f => f.type === 'phone'),
      },
      customFields: fields.filter(f => systemFields.indexOf(f.id) === -1),
      location: fields.find(f => f.id === 'booking_location'),
      guests: fields.find(f => f.id === 'booking_guests'),
    }

    return {
      date: data.date.value,
      serviceId: data.service.value,
      duration: data.duration.value,
      location: data.location.value,
      staffId: data.staff.value === 'any' ? null : data.staff.value,
      guests: data.guests ? data.guests.value : [],
      answers: data.customFields.map(f => ({
        id: f.id,
        question: f.question,
        answer: f.value,
      })),
      clientTimezone: timezone,
      customerId: rescheduling ? rescheduling.customer.id : null,
      customerName: data.customer.name ? data.customer.name.value : null,
      customerEmail: data.customer.email ? data.customer.email.value : null,
      customerPhone: data.customer.phone ? data.customer.phone.value : null,
    }
  }

  getThemedBookingForm() {
    const { business } = this.props

    let mainFontColor = '#4e9932'
    if (business && business.color) {
      mainFontColor = getContrastYIQ(business.color)
    }

    return styled.div`
      .booking-input-select ul.options li {
        color: ${mainFontColor};
        background: ${props => props.theme.main};
        &:hover, &.selected { background: ${props => props.theme.hover}; }
      }
      .booking-input-select div.checkbox .check { border-color: ${props => props.theme.main} }
      .booking-form-nav .nav-item.active { background: ${props => props.theme.main};  }
      .day-item:hover { border-color: ${props => props.theme.main}; }
      .day-selection-nav .month-nav {
        &:hover { border-color: ${props => props.theme.main}; }
        i { color: ${props => props.theme.main};  }
      }
      .time-selection {
        .time-item:hover { border-color: ${props => props.theme.main}; }
        .back { color: ${props => props.theme.main}; }
      }
      button.go-back { color: ${props => props.theme.main}; }
      .booking-input.inside-button button { background: ${props => props.theme.main};  }
      .btn-primary, .rbc-toolbar .rbc-btn-group:first-of-type button, .primary-bg {
        background: ${props => props.theme.main};
        border-color: ${props => props.theme.main};
        &:hover {
          background: ${props => props.theme.hover};
          border-color: ${props => props.theme.hover};
        }
      }
      .booking-summary strong { border-bottom-color: ${props => props.theme.main}; }
      .light-primary-background, .week-view .rbc-calendar .rbc-day-slot .rbc-event .rbc-event-content { background-color: ${props => Color(props.theme.main).fade(0.25).string()}; }
      .light-primary-background { background-color: ${props => Color(props.theme.main).fade(0.75).string()}!important; }
      .btn-primary, .booking-input.inside-button button { color: ${mainFontColor}; }
      .border-primary-color {
        border-color: ${props => props.theme.main}!important;
        &:hover { border-color: ${props => props.theme.hover}!important; }
      }
      .font-primary-color { color: ${props => props.theme.main}!important; }
      .react-datepicker__day--selected, .react-datepicker__day--in-selecting-range, .react-datepicker__day--in-range, .react-datepicker__month-text--selected, .react-datepicker__month-text--in-selecting-range, .react-datepicker__month-text--in-range, .react-datepicker__quarter-text--selected, .react-datepicker__quarter-text--in-selecting-range, .react-datepicker__quarter-text--in-range, .react-datepicker__year-text--selected, .react-datepicker__year-text--in-selecting-range, .react-datepicker__year-text--in-range, .react-datepicker__day--keyboard-selected, .react-datepicker__month-text--keyboard-selected, .react-datepicker__quarter-text--keyboard-selected, .react-datepicker__year-text--keyboard-selected, .coupon-code .coupon-input button, .coupon-code .coupon-details button { background: ${props => props.theme.main}; }
    `
  }

  getStepTitle() {
    const serviceField = this.state.fields.find(f => f.id === 'booking_service')
    if (!serviceField || !serviceField.value) {
      return null
    }

    const serviceId = serviceField.value
    const service = this.props.business.services.find(s => s.id === serviceId)

    if (!service) {
      return null
    }

    return (
      <div className='step-title'>
        { service.name }
        { service.description && <small>{ service.description }</small> }
      </div>
    )
  }



  renderBookingSummary() {
    const { business } = this.props
    const { themedBookingForm: ThemedBookingForm, fields } = this.state
    const goBack = this.props.step >= 1 ? this.goBack : null

    const serviceId = fields.find(f => f.id === 'booking_service').value
    const service = business.services.find(s => s.id === serviceId) || {}
    const durationValue = fields.find(f => f.id === 'booking_duration').value
    const payOnArrival = requiresPayOnArrival(service, durationValue)

    return (
      <ThemedBookingForm className="booking-form">
        <BookingFormHeader business={business} fields={this.state.fields} goBack={goBack} />
        <BookingSummary
          business={this.props.business}
          booking={this.fieldsToAppointment()}
          fields={this.state.fields}
          navigate={this.navigate}
          onSave={this.props.onSave}
          rescheduling={this.props.rescheduling}
          rescheduleFutureOccurrences={this.props.rescheduleFutureOccurrences}
          payOnArrival={payOnArrival}
        />
      </ThemedBookingForm>
    )
  }

  render() {
    const { business, rescheduling, countryCode, step } = this.props
    const { fields, leavingStep, themedBookingForm: ThemedBookingForm } = this.state
    const activeFields = fields.filter(f => !f.hidden)
    const currentField = activeFields.filter(f => !f.hidden)[step]
    const goBack = step >= 1 ? this.goBack : null
    const finished = (step >= fields.filter(f => !f.hidden).length)

    // Sometimes the fields need another cycle to render
    if (fields.length === 0) {
      return null
    }

    if (finished) {
      return this.renderBookingSummary()
    }

    const stepNumber = step + 1

    return (
      <ThemedBookingForm className="booking-form">
        <BookingFormNavigation fields={activeFields} activeStep={step} />
        <BookingFormHeader business={business} fields={fields} goBack={goBack} />
        {currentField &&
          <BookingFormStep
            leavingStep={leavingStep}
            field={currentField}
            step={stepNumber}
            goBack={goBack}
            fields={fields}
            business={business}
            onSubmit={this.submitAnswer}
            fieldsToAppointment={this.fieldsToAppointment}
            rescheduling={rescheduling}
            countryCode={countryCode}
            title={this.getStepTitle()}
          />
        }
      </ThemedBookingForm>
    )
  }
}

const mapStateToProps = (state) => ({
  // The page state is immutable, but we build on top of it using answers
  answers: {
    ...state.page,
    ...state.answers,
  },
  step: state.step,
  timezone: state.timezone,
  reset: state.reset,
})
export default connect(mapStateToProps)(translate("translations")(BookingForm))
